import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TRANSLATIONS, SUPPORTED_LANGS, DEFAULT_LANG } from '../i18n/translations';

const STORAGE_KEY = 'nro_lang';

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key) => key,
});

const detectInitialLang = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  } catch (_) {
    // ignore SSR / disabled-storage
  }
  if (typeof navigator !== 'undefined') {
    const nav = (navigator.language || navigator.userLanguage || '').toLowerCase();
    if (nav.startsWith('es')) return 'es';
  }
  return DEFAULT_LANG;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitialLang);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch (_) {
      // ignore
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED_LANGS.includes(next)) setLangState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = TRANSLATIONS[lang] || TRANSLATIONS[DEFAULT_LANG];
      let value = dict[key];
      if (value === undefined) {
        // fall back to default language so a missing translation never blanks the UI
        value = (TRANSLATIONS[DEFAULT_LANG] || {})[key];
      }
      if (value === undefined) return key;
      if (vars && typeof value === 'string') {
        return value.replace(/\{(\w+)\}/g, (_, k) =>
          vars[k] !== undefined ? String(vars[k]) : `{${k}}`
        );
      }
      return value;
    },
    [lang]
  );

  const ctx = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LanguageContext.Provider value={ctx}>{children}</LanguageContext.Provider>;
};

export const useLang = () => useContext(LanguageContext);
